import React, { useState, useCallback, useEffect } from 'react';
import { Table, Spinner, Alert, Form, Row, Col, Button, Pagination, Modal } from 'react-bootstrap';
import axios from 'axios';
import { format, addDays } from 'date-fns';
import * as XLSX from 'xlsx';

export const route = "/mimo/users";
export const role = "MIMO";

function UserReport() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [dateFilter, setDateFilter] = useState('All');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [username, setUsername] = useState('');
    const [sortField, setSortField] = useState('LAST_LOGIN_DATE');
    const [sortOrder, setSortOrder] = useState('DESC');
    const [showModal, setShowModal] = useState(false);
    const [entries, setEntries] = useState('all');
    const limit = 10;

    const handleExportClick = () => {
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
    };

    const handleExport = async () => {
        try {
            let startDate, endDate;
            const today = new Date();
            
            switch (dateFilter) {
                case 'today':
                    startDate = format(today, 'yyyy-MM-dd');
                    endDate = format(today, 'yyyy-MM-dd');
                    break;
                case 'yesterday':
                    const yesterday = addDays(today, -1);
                    startDate = format(yesterday, 'yyyy-MM-dd');
                    endDate = format(yesterday, 'yyyy-MM-dd');
                    break;
                case 'last7Days':
                    startDate = format(addDays(today, -7), 'yyyy-MM-dd');
                    endDate = format(today, 'yyyy-MM-dd');
                    break;
                case 'last30Days':
                    startDate = format(addDays(today, -30), 'yyyy-MM-dd');
                    endDate = format(today, 'yyyy-MM-dd');
                    break;
                case 'last3Months':
                    startDate = format(addDays(today, -90), 'yyyy-MM-dd');
                    endDate = format(today, 'yyyy-MM-dd');
                    break;
                case 'custom':
                    startDate = customStartDate;
                    endDate = customEndDate;
                    break;
                case 'All':
                    startDate = '1900-01-01';
                    endDate = '2099-12-31';
                    break;
                default:
                    startDate = format(addDays(today, -30), 'yyyy-MM-dd');
                    endDate = format(today, 'yyyy-MM-dd');
            }

            const startDateTime = `${startDate} 00:00:00`;
            const endDateTime = `${endDate} 23:59:59`;

            const url = `${process.env.REACT_APP_SNOWFLAKE_URL}/mimo/users/1/${entries === 'all' ? '99999999999999999' : entries}/${startDateTime}/${endDateTime}?username=${encodeURIComponent(username)}&sortField=${sortField}&sortOrder=${sortOrder}`;

            const response = await axios.get(url);
            let data = response.data;

            if (!data || data.length === 0) {
                alert('No data found for the selected criteria.');
                handleModalClose();
                return;
            }

            // Define headers for the Excel export
            const headers = [
                'User ID', 'User Name', 'Last Login Date', 'User Level', 
                'Inactive', 'Locked Out', 'Manager Only', 'Machine Operator', 'Create Date'
            ];

            // Format data for Excel
            const orderedData = data.map(user => [
                user.USER_ID,
                user.USER_NAME,
                user.LAST_LOGIN_DATE ? format(new Date(user.LAST_LOGIN_DATE), 'MM/dd/yyyy HH:mm') : 'Never',
                user.USER_LEVEL,
                user.F_INACTIVE ? 'Yes' : 'No',
                user.F_LOCKED_OUT ? 'Yes' : 'No',
                user.F_MANAGER_ONLY ? 'Yes' : 'No',
                user.F_MACHINE_OPERATOR ? 'Yes' : 'No',
                user.CREATE_DATE ? format(new Date(user.CREATE_DATE), 'MM/dd/yyyy') : ''
            ]);

            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.aoa_to_sheet([]);

            const adjustedStartDate = format(addDays(new Date(startDate), 1), 'MM/dd/yyyy');
            const adjustedEndDate = format(addDays(new Date(endDate), 1), 'MM/dd/yyyy');

            // Add metadata to the worksheet
            XLSX.utils.sheet_add_aoa(worksheet, [
                [`User Report`],
                [`Date Created: ${format(new Date(), 'MM/dd/yyyy')}`],
                [`Date Range: ${adjustedStartDate} - ${adjustedEndDate}`],
                []
            ], { origin: 'A1' });

            // Set headers on line 5
            XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: 'A5' });

            // Add the data starting on line 6
            XLSX.utils.sheet_add_aoa(worksheet, orderedData, { origin: 'A6' });

            // Set column widths
            worksheet['!cols'] = Array(headers.length).fill({ width: 15 });

            XLSX.utils.book_append_sheet(workbook, worksheet, 'User Report');
            XLSX.writeFile(workbook, `MIMO_User_Report_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`);

        } catch (error) {
            console.error('Export error:', error);
            alert('Failed to export data. Please try again.');
        }

        handleModalClose();
    };

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            let startDate, endDate;
            const today = new Date();

            switch (dateFilter) {
                case 'today':
                    startDate = format(today, 'yyyy-MM-dd');
                    endDate = format(today, 'yyyy-MM-dd');
                    break;
                case 'yesterday':
                    const yesterday = addDays(today, -1);
                    startDate = format(yesterday, 'yyyy-MM-dd');
                    endDate = format(yesterday, 'yyyy-MM-dd');
                    break;
                case 'last7Days':
                    startDate = format(addDays(today, -7), 'yyyy-MM-dd');
                    endDate = format(today, 'yyyy-MM-dd');
                    break;
                case 'last30Days':
                    startDate = format(addDays(today, -30), 'yyyy-MM-dd');
                    endDate = format(today, 'yyyy-MM-dd');
                    break;
                case 'last3Months':
                    startDate = format(addDays(today, -90), 'yyyy-MM-dd');
                    endDate = format(today, 'yyyy-MM-dd');
                    break;
                case 'custom':
                    startDate = customStartDate;
                    endDate = customEndDate;
                    break;
                case 'All':
                default:
                    startDate = '1900-01-01';
                    endDate = '2099-12-31';
            }

            const startDateTime = `${startDate} 00:00:00`;
            const endDateTime = `${endDate} 23:59:59`;

            const url = `${process.env.REACT_APP_SNOWFLAKE_URL}/mimo/users/${page}/${limit}/${startDateTime}/${endDateTime}?username=${encodeURIComponent(username)}&sortField=${sortField}&sortOrder=${sortOrder}`;

            const response = await axios.get(url);
            setUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
            setError('Failed to fetch user data');
        } finally {
            setLoading(false);
        }
    }, [dateFilter, page, limit, customStartDate, customEndDate, username, sortField, sortOrder]);

    useEffect(() => {
        fetchUsers();
    }, [page, limit, dateFilter, customStartDate, customEndDate, username, fetchUsers]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleSort = (field) => {
        setSortField(field);
        setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        return format(new Date(dateString), 'MM/dd/yyyy HH:mm');
    };

    return (
        <div className="container mt-5">
            <h1>User Report</h1>
            
            <Form>
                <Row className="mb-3 align-items-end">
                    <Col md={4}>
                        <Form.Group controlId="dateFilter">
                            <Form.Label>Last Login Date Filter</Form.Label>
                            <Form.Select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                                <option value="All">All</option>
                                <option value="today">Today</option>
                                <option value="yesterday">Yesterday</option>
                                <option value="last7Days">Last 7 Days</option>
                                <option value="last30Days">Last 30 Days</option>
                                <option value="last3Months">Last 3 Months</option>
                                <option value="custom">Custom</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={5}>
                        <Form.Group controlId="usernameFilter">
                            <Form.Label>Username</Form.Label>
                            <Form.Control 
                                type="text" 
                                value={username} 
                                onChange={(e) => setUsername(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        fetchUsers();
                                    }
                                }}
                                placeholder="Press Enter to search"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={3}>
                        <Button variant="success" onClick={handleExportClick} className="w-100">
                            Export to Excel
                        </Button>
                    </Col>
                </Row>
                {dateFilter === 'custom' && (
                    <Row className="mb-3">
                        <Col md={4}>
                            <Form.Group controlId="customStartDate">
                                <Form.Label>Start Date</Form.Label>
                                <Form.Control type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group controlId="customEndDate">
                                <Form.Label>End Date</Form.Label>
                                <Form.Control type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
                            </Form.Group>
                        </Col>
                    </Row>
                )}
            </Form>

            <Modal show={showModal} onHide={handleModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Export User Report</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group controlId="formEntries">
                        <Form.Label>Number of Entries</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter number of entries or 'all'"
                            value={entries}
                            onChange={(e) => setEntries(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleModalClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleExport}>
                        Export
                    </Button>
                </Modal.Footer>
            </Modal>

            {loading ? (
                <Spinner animation="border" variant="primary" />
            ) : error ? (
                <Alert variant="danger">{error}</Alert>
            ) : users.length === 0 ? (
                <Alert variant="info">No users found</Alert>
            ) : (
                <>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('USER_ID')} style={{cursor: 'pointer'}}>
                                    User ID {sortField === 'USER_ID' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('USER_NAME')} style={{cursor: 'pointer'}}>
                                    Username {sortField === 'USER_NAME' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('LAST_LOGIN_DATE')} style={{cursor: 'pointer'}}>
                                    Last Login {sortField === 'LAST_LOGIN_DATE' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('USER_LEVEL')} style={{cursor: 'pointer'}}>
                                    User Level {sortField === 'USER_LEVEL' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                                <th>Status</th>
                                <th onClick={() => handleSort('CREATE_DATE')} style={{cursor: 'pointer'}}>
                                    Created {sortField === 'CREATE_DATE' && (sortOrder === 'ASC' ? '↑' : '↓')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user) => (
                                <tr key={user.USER_ID}>
                                    <td>{user.USER_ID}</td>
                                    <td>{user.USER_NAME}</td>
                                    <td>{formatDate(user.LAST_LOGIN_DATE)}</td>
                                    <td>{user.USER_LEVEL}</td>
                                    <td>
                                        {user.F_INACTIVE && <span className="badge bg-warning me-1">Inactive</span>}
                                        {user.F_LOCKED_OUT && <span className="badge bg-danger me-1">Locked</span>}
                                        {user.F_MANAGER_ONLY && <span className="badge bg-info me-1">Manager</span>}
                                        {user.F_MACHINE_OPERATOR && <span className="badge bg-secondary me-1">Operator</span>}
                                        {!user.F_INACTIVE && !user.F_LOCKED_OUT && <span className="badge bg-success">Active</span>}
                                    </td>
                                    <td>{formatDate(user.CREATE_DATE)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <Pagination>
                        <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 1} />
                        <Pagination.Item active>{page}</Pagination.Item>
                        <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={users.length < limit} />
                    </Pagination>
                </>
            )}
        </div>
    );
}

export default UserReport;
